import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, MenuItem, IconButton } from "@mui/material";
import TextInput from "../../components/textInput";
import CustomButton from "../../components/customButton";
import CustomSelect from "../../components/customSelect";
import { useParams, useNavigate } from "react-router-dom";
import { createForm, updateForm, getFormById } from "../../services/forms";
import { openSnackAlert } from "../../helper";
import AppLoader from "../../components/appLoader";
import useUserStore from "../../zustand/useUserStore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const AddEditCustomForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const isEditMode = Boolean(id);

  const [formTitle, setFormTitle] = useState("");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentField, setCurrentField] = useState({
    type: "text",
    label: "",
    options: [],
  });
  const [optionInput, setOptionInput] = useState("");
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);

  useEffect(() => {
    if (isEditMode && id) {
      loadFormData();
    }
  }, [id, isEditMode]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      const response = await getFormById(id);
      if (response?.status === 200 || response?.status === 201) {
        const formData = response.data?.data || response.data;
        setFormTitle(formData?.title || "");
        setFields(formData?.fields || []);
      }
    } catch (error) {
      openSnackAlert("Error loading form data", "error");
    } finally {
      setLoading(false);
    }
  };

  const createOption = (label) => ({
    label: label.trim(),
    value: label.trim().toLowerCase().replace(/\s+/g, "-"),
  });

  const resetForm = () => {
    setCurrentField({ type: "text", label: "", options: [] });
    setOptionInput("");
    setEditingFieldIndex(null);
    setEditingOptionIndex(null);
  };

  const handleAddOption = () => {
    if (!optionInput.trim()) return;
    const newOption = createOption(optionInput);

    setCurrentField((prev) => ({
      ...prev,
      options:
        editingOptionIndex !== null
          ? prev.options.map((opt, i) =>
              i === editingOptionIndex ? newOption : opt
            )
          : [...prev.options, newOption],
    }));
    setOptionInput("");
    setEditingOptionIndex(null);
  };

  const handleAddField = () => {
    if (!currentField.label.trim()) {
      openSnackAlert("Please enter field label", "error");
      return;
    }
    if (
      (currentField.type === "radio" || currentField.type === "checkbox") &&
      currentField.options.length === 0
    ) {
      openSnackAlert("Please add at least one option", "error");
      return;
    }

    const updatedField = { ...currentField, label: currentField.label.trim() };
    setFields((prev) =>
      editingFieldIndex !== null
        ? prev.map((field, i) =>
            i === editingFieldIndex ? updatedField : field
          )
        : [...prev, updatedField]
    );
    resetForm();
  };

  const handleEditField = (index) => {
    const field = fields[index];
    setCurrentField({
      type: field.type,
      label: field.label,
      options: field.options || [],
    });
    setEditingFieldIndex(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formTitle.trim() || fields.length === 0) {
      openSnackAlert("Please fill all required fields", "error");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        title: formTitle.trim(),
        fields: fields.map((f) => ({
          label: f.label,
          type: f.type,
          required: false,
          options: f.options || [],
        })),
      };
      const response = isEditMode
        ? await updateForm(id, payload)
        : await createForm(payload);
      if (response?.status === 200 || response?.status === 201) {
        openSnackAlert(
          `Form ${isEditMode ? "updated" : "created"} successfully`,
          "success"
        );
        navigate("/custom-form");
      }
    } catch (error) {
      openSnackAlert("Error saving form", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mt={0}
    >
      <AppLoader isVisible={loading} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
            Add/Edit Custom Form
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextInput
            label="Form Title"
            name="formTitle"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder="Enter form title"
            fullWidth
            inputBgColor={"#fff"}
            borderColor={"#225B27"}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomSelect
            name="fieldType"
            label="Field Type"
            value={currentField.type}
            onChange={(e) =>
              setCurrentField((prev) => ({
                ...prev,
                type: e.target.value,
                options:
                  e.target.value === "text" || e.target.value === "images"
                    ? []
                    : prev.options,
              }))
            }
            placeholder="Select field type"
            InputWidth="100%"
            sx={{ width: "100%", height: "36px" }}
          >
            <MenuItem value="text">Text</MenuItem>
            <MenuItem value="radio">Radio</MenuItem>
            <MenuItem value="checkbox">Checkbox</MenuItem>
            <MenuItem value="images">Attachments</MenuItem>
          </CustomSelect>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextInput
            label="Label"
            name="fieldLabel"
            value={currentField.label}
            onChange={(e) =>
              setCurrentField((prev) => ({ ...prev, label: e.target.value }))
            }
            placeholder="Enter field label"
            fullWidth
            inputBgColor={"#fff"}
            borderColor={"#225B27"}
          />
        </Grid>

        {(currentField.type === "radio" ||
          currentField.type === "checkbox") && (
          <Grid item xs={12}>
            <TextInput
              label="Options"
              name="optionInput"
              value={optionInput}
              onChange={(e) => setOptionInput(e.target.value)}
              placeholder="Option label"
              fullWidth
              inputBgColor={"#fff"}
              borderColor={"#225B27"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
            />
            <Box mt={1} display="flex" justifyContent="flex-end">
              <CustomButton
                btnLabel={
                  editingOptionIndex !== null ? "Update Option" : "+ Add Option"
                }
                handlePressBtn={handleAddOption}
                btnBgColor="#225B27"
                btnTextColor="#fff"
                width="auto"
              />
            </Box>
            {currentField.options.length > 0 && (
              <Box mt={2} display="flex" flexDirection="column" gap={1}>
                {currentField.options.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="body2">{option.label}</Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setOptionInput(option.label);
                          setEditingOptionIndex(index);
                        }}
                        sx={{ color: "#225B27" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCurrentField((prev) => ({
                            ...prev,
                            options: prev.options.filter((_, i) => i !== index),
                          }));
                          if (editingOptionIndex === index) resetForm();
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            {editingFieldIndex !== null && (
              <CustomButton
                btnLabel="Cancel"
                handlePressBtn={resetForm}
                btnBgColor="#E0E0E0"
                btnTextColor="#222"
                width="auto"
              />
            )}
            <CustomButton
              btnLabel={
                editingFieldIndex !== null ? "Update Field" : "Add Field"
              }
              handlePressBtn={handleAddField}
              btnBgColor="#4A4A4A"
              btnTextColor="#fff"
              width="fit-content"
            />
          </Box>
        </Grid>

        {fields.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
              Added Fields ({fields.length})
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {fields.map((field, index) => (
                <Box
                  key={index}
                  sx={{
                    padding: "12px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="start"
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {field.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={0.5}
                      >
                        Type: {field.type.toUpperCase()}
                      </Typography>
                      {field.options?.length > 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          Options:{" "}
                          {field.options.map((opt) => opt.label).join(", ")}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditField(index)}
                        sx={{ color: "#225B27", fontSize: "16px" }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setFields((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        sx={{ color: "#e53935" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <CustomButton
              btnLabel="Cancel"
              btnBgColor="#E0E0E0"
              btnTextColor="#222"
              width="auto"
              handlePressBtn={() => navigate("/custom-form")}
            />
            <CustomButton
              type="submit"
              handlePressBtn={handleSubmit}
              btnLabel={isEditMode ? "Update Form" : "Save Form"}
              btnBgColor="#225B27"
              btnTextColor="#fff"
              width="auto"
              disabled={loading}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddEditCustomForm;
